import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

const HERO = "/login-hero.jpg";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.2fr_1fr]">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Gym weight plates" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050D24] via-[#050D24]/60 to-[#050D24]/20" />
        <img src="/brand/leeway-logo-light.png" alt="Leeway Fitness" className="absolute left-12 top-10 h-20 w-auto" />
        <div className="absolute bottom-0 left-0 p-12 max-w-xl fade-up">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary">Inventory command center</div>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white">
            Every plate, bar & bench — counted daily.
          </h1>
          <p className="mt-4 text-slate-200 text-base">Inward, outward, orders, reorder levels and forecasts across Warehouse A and B.</p>
        </div>
      </div>
      <div className="flex items-center justify-center bg-white p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up" data-testid="login-form">
          <div className="flex flex-col items-start gap-2">
            <img src="/brand/leeway-logo.png" alt="Leeway Fitness" className="h-24 w-auto" data-testid="login-logo" />
            <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Stock Control</div>
          </div>
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-1">Accounts are created by your administrator.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
          </div>
          {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">{error}</div>}
          <Button type="submit" className="w-full h-10 font-semibold" disabled={busy} data-testid="login-submit-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
          </Button>
        </form>
      </div>
    </div>
  );
}
